import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { AlertTriangle, ChevronLeft, ChevronRight, ClipboardList, Send, Save } from 'lucide-react'
import { PageHeader, DateValue, EmptyState, ErrorState, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useHistory, useProjects, useTimesheetMutations, useWeek } from '../api'
import { WeekGrid } from '../components/WeekGrid'
import { addDays, gridProblems, gridTotal, toLines, toRows, weekDates, weekStartFor, type GridRow } from '../lib/grid'
import type { WeekView } from '../types'

function todayIso(): string {
  return todayLocal()
}

const STATUS_NOTE: Record<WeekView['status'], string | null> = {
  DRAFT: null,
  PENDING: 'Waiting on your approver. The week is locked until they decide.',
  APPROVED: 'Approved. These hours are final.',
  REJECTED: 'Rejected — talk to your approver before resubmitting.',
  RETURNED: 'Sent back for changes. Amend the week and submit it again.',
}

/**
 * The weekly timesheet.
 *
 * <p>Time is entered as a grid rather than a list of entries because that is how people
 * reconstruct a week: project by project, across the days. The sheet is one row per
 * (employee, week), so navigating weeks reloads rather than accumulating.
 */
export function TimesheetPage() {
  const [anchor, setAnchor] = useState(() => weekStartFor(todayIso()))
  const [rows, setRows] = useState<GridRow[]>([])
  const [dirty, setDirty] = useState(false)
  const [loadedSheetId, setLoadedSheetId] = useState<string | null>(null)
  const breadcrumb = useBreadcrumb()

  const week = useWeek(anchor)
  const projects = useProjects()
  const history = useHistory()
  const actions = useTimesheetMutations()

  // The server owns the week. Loading a different sheet replaces the grid outright,
  // adjusted during render rather than in an effect so the stale week is never painted.
  // Saves and submissions push their own response back through `adopt` below, which is
  // why a refetch of the same sheet does not overwrite what is being typed.
  if (week.data && week.data.id !== loadedSheetId) {
    setLoadedSheetId(week.data.id)
    setRows(toRows(week.data.lines))
    setDirty(false)
  }

  function adopt(sheet: WeekView) {
    setLoadedSheetId(sheet.id)
    setRows(toRows(sheet.lines))
    setDirty(false)
  }

  const dates = week.data ? weekDates(week.data.weekStart) : weekDates(anchor)
  const editable = week.data?.editable ?? false
  const problems = gridProblems(rows, dates)
  const statusNote = week.data ? STATUS_NOTE[week.data.status] : null

  function move(days: number) {
    setAnchor((current) => addDays(current, days))
  }

  function save() {
    actions.saveWeek.mutate({ weekOf: week.data?.weekStart ?? anchor, lines: toLines(rows) }, { onSuccess: adopt })
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Timesheets"
        subtitle="Record the week, then send it for approval"
        breadcrumb={breadcrumb}
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" aria-label="Previous week" onClick={() => move(-7)}>
              <ChevronLeft aria-hidden="true" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(weekStartFor(todayIso()))}>
              This week
            </Button>
            <Button variant="outline" size="sm" aria-label="Next week" onClick={() => move(7)}>
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        }
      />

      {week.isLoading && <Skeleton className="h-72 w-full rounded-[var(--radius-card)]" />}
      {week.error != null && <ErrorState error={week.error} onRetry={() => void week.refetch()} />}

      {week.data && (
        <Card className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-heading)]">
                <DateValue value={week.data.weekStart} /> – <DateValue value={week.data.weekEnd} />
              </h2>
              <p className="mt-1 text-sm text-[var(--color-body)]">
                <span className="font-semibold tabular-nums text-[var(--color-heading)]">
                  {gridTotal(rows)}
                </span>{' '}
                hours
                {week.data.billableHours > 0 && (
                  <> · {week.data.billableHours} billable</>
                )}
              </p>
            </div>
            <StatusBadge status={week.data.status} />
          </div>

          {statusNote && (
            <p className="mt-3 text-sm text-[var(--color-body)]">
              {statusNote}
              {week.data.decisionComment && (
                <span className="mt-1 block italic text-[var(--color-muted)]">
                  “{week.data.decisionComment}”
                </span>
              )}
            </p>
          )}

          {projects.data?.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No projects accept time yet"
              description="Time is booked against a project, so somebody needs to open one before the week can be filled in."
            />
          ) : (
            <div className="mt-4">
              <WeekGrid
                rows={rows}
                dates={dates}
                projects={projects.data ?? []}
                editable={editable}
                onChange={(next) => {
                  setRows(next)
                  setDirty(true)
                }}
              />
            </div>
          )}

          {problems.length > 0 && (
            <ul role="alert" className="mt-3 space-y-1">
              {problems.map((problem) => (
                <li key={problem} className="flex items-start gap-2 text-sm text-[var(--color-danger)]">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  {problem}
                </li>
              ))}
            </ul>
          )}

          {(actions.saveWeek.error != null || actions.submit.error != null) && (
            <ErrorState error={actions.saveWeek.error ?? actions.submit.error} />
          )}

          {editable && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={save} disabled={problems.length > 0 || actions.saveWeek.isPending}>
                <Save aria-hidden="true" />
                {actions.saveWeek.isPending ? 'Saving…' : 'Save draft'}
              </Button>
              <Button
                variant="outline"
                disabled={dirty || gridTotal(rows) === 0 || actions.submit.isPending}
                title={dirty ? 'Save the week before submitting it' : undefined}
                onClick={() => actions.submit.mutate(week.data.id, { onSuccess: adopt })}
              >
                <Send aria-hidden="true" />
                {actions.submit.isPending ? 'Submitting…' : 'Submit for approval'}
              </Button>
              {dirty && (
                <span className="self-center text-xs text-[var(--color-muted)]">Unsaved changes</span>
              )}
            </div>
          )}
        </Card>
      )}

      {history.data != null && history.data.length > 0 && (
        <Card>
          <h2 className="border-b border-[var(--color-border)] px-4 py-3 text-sm font-semibold text-[var(--color-heading)]">
            Recent weeks
          </h2>
          <ul className="divide-y divide-[var(--color-border)]">
            {history.data.map((sheet) => (
              <li key={sheet.id}>
                <button
                  type="button"
                  onClick={() => setAnchor(sheet.weekStart)}
                  className="flex w-full items-center gap-4 px-4 py-3 text-start text-sm hover:bg-[var(--color-surface)]"
                >
                  <span className="min-w-0 flex-1 truncate text-[var(--color-heading)]">
                    <DateValue value={sheet.weekStart} /> – <DateValue value={sheet.weekEnd} />
                  </span>
                  <span className="shrink-0 tabular-nums text-[var(--color-body)]">{sheet.totalHours}h</span>
                  <StatusBadge status={sheet.status} />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
